import { supabase, DEFAULT_ORG_ID, applyOrgFilter, signImagesDeep } from '../supabaseClient';
import { getMexicoDateString } from '../dateUtils';

export const getAsistenciasBySesion = async (sesionId) => {
  if (!sesionId) return [];
  let query = supabase
    .from('asistencias')
    .select(`
      *,
      jugador:jugadores(id, nombre_completo, nickname, numero, imagen_url, imagen_path),
      staff:staff!asistencias_staff_id_fkey(id, nombre_completo)
    `)
    .eq('sesion_id', sesionId);
  query = applyOrgFilter(query);
  const { data, error } = await query;

  if (error) {
    console.error('[getAsistenciasBySesion] Error:', error);
    throw error;
  }
  await signImagesDeep(data);
  return data || [];
};

export const getAsistenciasCountsBySesiones = async (sesionIds = []) => {
  if (!Array.isArray(sesionIds) || sesionIds.length === 0) return { data: {}, error: null };
  try {
    let query = supabase
      .from('asistencias')
      .select('sesion_id')
      .in('sesion_id', sesionIds);
    query = applyOrgFilter(query);
    const { data, error } = await query;

    if (error) throw error;
    const counts = (data || []).reduce((acc, row) => {
      if (!row?.sesion_id) return acc;
      acc[row.sesion_id] = (acc[row.sesion_id] || 0) + 1;
      return acc;
    }, {});
    return { data: counts, error: null };
  } catch (error) {
    return { data: {}, error };
  }
};

export const getSesionesByGrupo = async ({ groupId, startDate, endDate, tipo = null } = {}) => {
  if (!groupId) return { data: [], error: null };
  let query = supabase
    .from('sesiones')
    .select(`
      *,
      serie_entrenamiento:series_entrenamiento(id, nombre, descripcion, hora_inicio, hora_fin),
      juego:juegos(id, nombre, fecha, hora, estatus)
    `)
    .eq('grupo_id', groupId)
    .order('fecha', { ascending: true });

  if (startDate) query = query.gte('fecha', startDate);
  if (endDate) query = query.lte('fecha', endDate);
  if (tipo) query = query.eq('tipo', tipo);

  query = applyOrgFilter(query);
  const { data, error } = await query;
  if (error) {
    console.error('[getSesionesByGrupo] Error:', error);
    return { data: [], error };
  }
  return { data: data || [], error: null };
};

export const getSesionById = async (sesionId) => {
  if (!sesionId) return { data: null, error: null };
  let query = supabase
    .from('sesiones')
    .select(`
      *,
      serie_entrenamiento:series_entrenamiento(id, nombre, descripcion, hora_inicio, hora_fin),
      juego:juegos(id, nombre, fecha, hora, estatus)
    `)
    .eq('id', sesionId);
  query = applyOrgFilter(query);
  const { data, error } = await query.single();

  if (error) return { data: null, error };
  return { data, error: null };
};

export const updateSesion = async (sesionId, changes = {}) => {
  if (!sesionId) return { data: null, error: new Error('sesionId es requerido') };
  const { data, error } = await supabase
    .from('sesiones')
    .update(changes)
    .eq('id', sesionId)
    .select()
    .single();

  if (error) return { data: null, error };
  return { data, error: null };
};

export const getPrevSesionConAsistencia = async ({ groupId, beforeDate } = {}) => {
  if (!groupId || !beforeDate) return { data: null, error: null };

  let query = supabase
    .from('asistencias')
    .select(
      `
      fecha,
      sesion:sesiones(id, tipo, estatus, fecha)
    `
    )
    .eq('grupo_id', groupId)
    .not('sesion_id', 'is', null)
    .lt('fecha', beforeDate)
    .eq('sesion.tipo', 'entrenamiento')
    .neq('sesion.estatus', 'cancelado')
    .order('fecha', { ascending: false })
    .limit(1);
  query = applyOrgFilter(query);
  const { data, error } = await query;

  if (error) return { data: null, error };
  return { data: Array.isArray(data) && data.length > 0 ? data[0] : null, error: null };
};

export const getSesionConAsistenciaByFecha = async ({ groupId, fecha } = {}) => {
  if (!groupId || !fecha) return { data: null, error: null };

  let query = supabase
    .from('asistencias')
    .select(
      `
      fecha,
      sesion:sesiones(id, tipo, estatus, fecha)
    `
    )
    .eq('grupo_id', groupId)
    .not('sesion_id', 'is', null)
    .eq('fecha', fecha)
    .eq('sesion.tipo', 'entrenamiento')
    .order('created_at', { ascending: false })
    .limit(1);
  query = applyOrgFilter(query);
  const { data, error } = await query;

  if (error) return { data: null, error };
  return { data: Array.isArray(data) && data.length > 0 ? data[0] : null, error: null };
};

export const getSesionPreferidaPorFecha = async ({ groupId, fecha } = {}) => {
  if (!groupId || !fecha) return { data: null, error: null };

  let sesionesQuery = supabase
    .from('sesiones')
    .select('id, fecha, tipo, estatus, updated_at, created_at')
    .eq('grupo_id', groupId)
    .eq('tipo', 'entrenamiento')
    .eq('fecha', fecha)
    .order('updated_at', { ascending: false })
    .order('created_at', { ascending: false });
  sesionesQuery = applyOrgFilter(sesionesQuery);
  const { data: sesiones, error: sesionesError } = await sesionesQuery;

  if (sesionesError) return { data: null, error: sesionesError };
  if (!sesiones || sesiones.length === 0) return { data: null, error: null };

  const sessionIds = sesiones.map((s) => s.id);
  let asistenciasQuery = supabase
    .from('asistencias')
    .select('sesion_id, created_at')
    .eq('grupo_id', groupId)
    .in('sesion_id', sessionIds)
    .order('created_at', { ascending: false });
  asistenciasQuery = applyOrgFilter(asistenciasQuery);
  const { data: asistencias, error: asistenciasError } = await asistenciasQuery;

  if (asistenciasError) return { data: sesiones[0], error: null };

  if (asistencias && asistencias.length > 0) {
    const firstWithAttendance = sesiones.find((sesion) =>
      asistencias.some((a) => a.sesion_id === sesion.id)
    );
    return { data: firstWithAttendance || sesiones[0], error: null };
  }

  return { data: sesiones[0], error: null };
};

export const getOrCreateSesionEntrenamiento = async ({
  groupId,
  fecha,
  serieId = null
} = {}) => {
  if (!groupId || !fecha) {
    return { data: null, error: new Error('groupId y fecha son requeridos') };
  }

  const normalizedFecha = getMexicoDateString(fecha);

  if (serieId) {
    let serieCheckQuery = supabase
      .from('series_entrenamiento')
      .select('id, activo')
      .eq('id', serieId)
      .eq('grupo_id', groupId);
    serieCheckQuery = applyOrgFilter(serieCheckQuery);
    const { data: serieCheck, error: serieCheckError } = await serieCheckQuery.maybeSingle();

    if (serieCheckError) return { data: null, error: serieCheckError };
    if (serieCheck && serieCheck.activo !== true) {
      return { data: null, error: new Error('La serie asignada está inactiva') };
    }
  }

  let existingAnyQuery = supabase
    .from('sesiones')
    .select('*')
    .eq('grupo_id', groupId)
    .eq('fecha', normalizedFecha)
    .eq('tipo', 'entrenamiento')
    .order('updated_at', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(1);
  existingAnyQuery = applyOrgFilter(existingAnyQuery);
  const { data: existingAny, error: existingAnyError } = await existingAnyQuery;

  if (existingAnyError) return { data: null, error: existingAnyError };
  if (Array.isArray(existingAny) && existingAny.length > 0) {
    return { data: existingAny[0], error: null };
  }

  let seriesQuery = supabase
    .from('series_entrenamiento')
    .select('id, nombre, dias_semana, fecha_inicio, fecha_fin, hora_inicio, hora_fin, activo')
    .eq('grupo_id', groupId)
    .eq('activo', true)
    .order('fecha_inicio', { ascending: false });
  seriesQuery = applyOrgFilter(seriesQuery);
  const { data: series, error: seriesError } = await seriesQuery;

  if (seriesError) return { data: null, error: seriesError };

  const dateObj = new Date(`${normalizedFecha}T00:00:00`);
  const dayIndex = dateObj.getDay();
  const dayNames = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
  const dayKey = dayNames[dayIndex];

  let serie = null;
  if (serieId) {
    serie = (series || []).find((item) => item.id === serieId) || null;
  }

  if (!serie) {
    serie = (series || []).find((item) => {
      const withinStart = !item.fecha_inicio || item.fecha_inicio <= normalizedFecha;
      const withinEnd = !item.fecha_fin || item.fecha_fin >= normalizedFecha;
      const inDay = Array.isArray(item.dias_semana) && item.dias_semana.includes(dayKey);
      return withinStart && withinEnd && inDay;
    }) || null;
  }

  if (!serie && (series || []).length > 0) {
    // Fallback: usar la serie más reciente para crear sesión extraordinaria
    serie = series[0];
  }

  if (!serie) {
    // Grupo sin horarios (series) activos: crear sesión suelta para no
    // bloquear la toma de asistencia. org_id lo sincroniza el trigger.
    const { data, error } = await supabase
      .from('sesiones')
      .insert({
        tipo: 'entrenamiento',
        grupo_id: groupId,
        fecha: normalizedFecha,
        hora_inicio: null,
        hora_fin: null,
        titulo: 'Entrenamiento',
        estatus: 'programado',
        serie_entrenamiento_id: null,
        es_extraordinaria: true
      })
      .select()
      .single();

    if (error) return { data: null, error };
    return { data, error: null };
  }

  let existingQuery = supabase
    .from('sesiones')
    .select('*')
    .eq('grupo_id', groupId)
    .eq('fecha', normalizedFecha)
    .eq('tipo', 'entrenamiento')
    .eq('serie_entrenamiento_id', serie.id);
  existingQuery = applyOrgFilter(existingQuery);
  const existing = await existingQuery.maybeSingle();

  if (existing?.data) {
    return { data: existing.data, error: null };
  }
  if (existing?.error && existing.error.code !== 'PGRST116') {
    return { data: null, error: existing.error };
  }

  const inDay = Array.isArray(serie.dias_semana) && serie.dias_semana.includes(dayKey);
  const withinStart = !serie.fecha_inicio || serie.fecha_inicio <= normalizedFecha;
  const withinEnd = !serie.fecha_fin || serie.fecha_fin >= normalizedFecha;
  const esExtra = !(inDay && withinStart && withinEnd);

  const payload = {
    tipo: 'entrenamiento',
    grupo_id: groupId,
    fecha: normalizedFecha,
    hora_inicio: serie.hora_inicio || null,
    hora_fin: serie.hora_fin || null,
    titulo: serie.nombre || 'Entrenamiento',
    estatus: 'programado',
    serie_entrenamiento_id: serie.id,
    es_extraordinaria: esExtra
  };

  const { data, error } = await supabase
    .from('sesiones')
    .insert(payload)
    .select()
    .single();

  if (error) return { data: null, error };
  return { data, error: null };
};
